import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router'
import moment from 'moment-timezone'
import {
  MagnifyingGlassIcon,
  CaretDownIcon,
  PlusIcon,
  DownloadSimpleIcon,
} from '@phosphor-icons/react'
import { useLazyGetAllEngagementsQuery } from '../../redux/engagement.endpoints'
import { Engagement } from '../../types/Engagement'
import EngagementsTable, {
  getEngagementStatus,
} from '../../components/EngagementsTable/EngagementsTable'
import * as XLSX from 'xlsx'

interface Props {
  companyId: number
}

const EngagementsView = ({ companyId }: Props) => {
  const navigate = useNavigate()
  const [engagements, setEngagements] = useState([] as Engagement[])
  const [searchQuery, setSearchQuery] = useState('')
  const [clientFilter, setClientFilter] = useState('All Clients')
  const [teamFilter, setTeamFilter] = useState('All Teams')
  const [statusFilter, setStatusFilter] = useState('All Statuses')
  const [getEngagements, { data }] = useLazyGetAllEngagementsQuery()

  useEffect(() => {
    getEngagements({
      companyId,
      pageSize: 0,
      pageNumber: 0,
      sortBy: 'id',
      searchQuery: '',
      sortType: 'DESC',
    })
  }, [companyId, getEngagements])

  useEffect(() => {
    if (data) {
      setEngagements(data)
    }
  }, [data])

  const clients = useMemo(
    () => [
      'All Clients',
      ...Array.from(
        new Set(
          engagements
            .map(e => e.client?.name)
            .filter((name): name is string => Boolean(name)),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    ],
    [engagements],
  )

  const teams = useMemo(
    () => [
      'All Teams',
      ...Array.from(
        new Set(
          engagements
            .map(e => e.team?.name)
            .filter((name): name is string => Boolean(name)),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    ],
    [engagements],
  )

  const filteredEngagements = useMemo(() => {
    return engagements.filter(engagement => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        const projectName = engagement.projectName?.toLowerCase() || ''
        const clientName = engagement.client?.name?.toLowerCase() || ''
        if (!projectName.includes(q) && !clientName.includes(q)) {
          return false
        }
      }
      if (
        clientFilter !== 'All Clients' &&
        engagement.client?.name !== clientFilter
      ) {
        return false
      }
      if (teamFilter !== 'All Teams' && engagement.team?.name !== teamFilter) {
        return false
      }
      if (statusFilter !== 'All Statuses') {
        if (getEngagementStatus(engagement) !== statusFilter) {
          return false
        }
      }
      return true
    })
  }, [engagements, searchQuery, clientFilter, teamFilter, statusFilter])

  const downloadReport = () => {
    const dataToExport = filteredEngagements.map(engagement => ({
      'Team Code Name': engagement.projectName,
      Client: engagement.client?.name || '',
      Team: engagement.team?.name || '',
      'Start Date': moment(engagement.startDate).format('MM/DD/YYYY'),
      'End Date': engagement.endDate
        ? moment(engagement.endDate).format('MM/DD/YYYY')
        : '',
      Status: getEngagementStatus(engagement),
    }))

    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(dataToExport)
    XLSX.utils.book_append_sheet(wb, ws, 'Engagements')
    XLSX.writeFile(wb, 'engagements_report.xlsx')
  }

  const goToEngagement = (engagement: Engagement) => {
    navigate(`/engagements/${engagement.id}`)
  }

  return (
    <div className="container my-4 management-engagement">
      <Helmet title="Time Off - Engagements" />

      <div className="management-engagement__header">
        <h1 className="management-engagement__title">Manage Engagements</h1>
        <div className="management-engagement__actions">
          <button
            type="button"
            className="management-engagement__btn management-engagement__btn--secondary"
            onClick={downloadReport}
          >
            <DownloadSimpleIcon size={16} weight="regular" color="#0A0A0A" />
            Download Report
          </button>
          <button
            type="button"
            className="management-engagement__btn management-engagement__btn--primary"
            onClick={() => navigate('/engagements/create')}
          >
            <PlusIcon size={16} weight="regular" color="#ffffff" />
            Add New Engagement
          </button>
        </div>
      </div>

      <div className="management-engagement__filters">
        <div className="management-engagement__search">
          <input
            type="text"
            className="management-engagement__search-input"
            placeholder="Search by Team Code or Client"
            aria-label="Search by Team Code or Client"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <MagnifyingGlassIcon
            size={16}
            weight="regular"
            className="management-engagement__search-icon"
          />
        </div>

        <div className="management-engagement__filter-row">
          <div className="management-engagement__filter">
            <span className="management-engagement__filter-label">Client</span>
            <div className="management-engagement__filter-select-wrap">
              <select
                className="management-engagement__filter-select"
                value={clientFilter}
                onChange={e => setClientFilter(e.target.value)}
              >
                {clients.map(client => (
                  <option key={client} value={client}>
                    {client}
                  </option>
                ))}
              </select>
              <CaretDownIcon
                size={14}
                weight="regular"
                className="management-engagement__filter-caret"
              />
            </div>
          </div>

          <div className="management-engagement__filter">
            <span className="management-engagement__filter-label">Team</span>
            <div className="management-engagement__filter-select-wrap">
              <select
                className="management-engagement__filter-select"
                value={teamFilter}
                onChange={e => setTeamFilter(e.target.value)}
              >
                {teams.map(team => (
                  <option key={team} value={team}>
                    {team}
                  </option>
                ))}
              </select>
              <CaretDownIcon
                size={14}
                weight="regular"
                className="management-engagement__filter-caret"
              />
            </div>
          </div>

          <div className="management-engagement__filter">
            <span className="management-engagement__filter-label">Status</span>
            <div className="management-engagement__filter-select-wrap">
              <select
                className="management-engagement__filter-select"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
              >
                {['All Statuses', 'Active', 'Inactive'].map(status => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <CaretDownIcon
                size={14}
                weight="regular"
                className="management-engagement__filter-caret"
              />
            </div>
          </div>
        </div>
      </div>

      <EngagementsTable
        engagements={filteredEngagements}
        onRowClick={goToEngagement}
        onEdit={goToEngagement}
      />
    </div>
  )
}

export default EngagementsView
